import React, { FC, useState, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Chip,
  Button,
  CircularProgress,
} from '@mui/material'
import { Sms, Email, Smartphone, CheckCircle } from '@mui/icons-material'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import OtpInput from 'react-otp-input'
import TotpSetup from 'components/totp-setup'
import { CustomAlert } from 'components'
import { FactorType } from 'models/Mfa.d'
import useStyles from './factor-enrollment-card-styles'

export type EnrollmentStatus =
  | 'not_enrolled'
  | 'input'
  | 'verifying'
  | 'enrolled'

export interface FactorEnrollmentState {
  status: EnrollmentStatus
  phoneNumber?: string
  email?: string
  verificationCode?: string
  qrCode?: string
  secret?: string
  issuer?: string
  error?: string
}

interface FactorEnrollmentCardProps {
  factorType: FactorType
  isRequired: boolean
  state: FactorEnrollmentState
  onStateChange: (state: FactorEnrollmentState) => void
  onEnroll: (
    factorType: FactorType,
    data: { phoneNumber?: string; email?: string },
  ) => void
  onVerify: (factorType: FactorType, code: string) => void
  onResend?: (factorType: FactorType) => Promise<void> | void
  disabled?: boolean
  showLoader?: boolean
  userEmail?: string
  locked?: boolean
}

const getFactorIcon = (type: FactorType) => {
  const iconProps = { fontSize: 'medium' as const }
  switch (type) {
    case 'sms':
      return <Sms {...iconProps} />
    case 'email':
      return <Email {...iconProps} />
    case 'totp':
      return <Smartphone {...iconProps} />
    default:
      return null
  }
}

const getFactorDisplayName = (type: FactorType): string => {
  switch (type) {
    case 'sms':
      return 'SMS Text Message'
    case 'email':
      return 'Email'
    case 'totp':
      return 'Authenticator App'
    default:
      return type
  }
}

const getFactorDescription = (type: FactorType, userEmail?: string): string => {
  switch (type) {
    case 'sms':
      return 'Receive verification codes via text message'
    case 'email':
      return userEmail
        ? `Send verification codes to ${userEmail}`
        : 'Receive verification codes via email'
    case 'totp':
      return 'Use Google Authenticator, Authy, or similar app'
    default:
      return ''
  }
}

const FactorEnrollmentCard: FC<FactorEnrollmentCardProps> = ({
  factorType,
  isRequired,
  state,
  onStateChange,
  onEnroll,
  onVerify,
  onResend,
  disabled = false,
  showLoader = false,
  userEmail,
  locked = false,
}) => {
  const { classes, cx } = useStyles()
  const [phoneNumber, setPhoneNumber] = useState(state.phoneNumber || '')
  const [email, setEmail] = useState(state.email || '')
  const [verificationCode, setVerificationCode] = useState(
    state.verificationCode || '',
  )
  const [country, setCountry] = useState<string>('US')
  const [isPhoneValid, setIsPhoneValid] = useState(false)
  const [isEmailValid, setIsEmailValid] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [resendDisabled, setResendDisabled] = useState(false)

  const isEnrolled = state.status === 'enrolled'
  const isVerifying = state.status === 'verifying'
  const isInputMode = state.status === 'input'

  // Validate phone number
  useEffect(() => {
    if (phoneNumber) {
      if (country === 'US' || country === 'CA') {
        setIsPhoneValid(phoneNumber.length === 12)
      } else {
        setIsPhoneValid(isValidPhoneNumber(phoneNumber))
      }
    } else {
      setIsPhoneValid(false)
    }
  }, [phoneNumber, country])

  // Validate email
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    setIsEmailValid(emailRegex.test(email))
  }, [email])

  const handleStartEnrollment = () => {
    if (factorType === 'totp') {
      // TOTP doesn't need input, go directly to enroll
      onEnroll(factorType, {})
    } else if (factorType === 'email' && userEmail) {
      // Email uses the user's Okta account email - auto-enroll without input
      onEnroll(factorType, { email: userEmail })
    } else {
      onStateChange({ ...state, status: 'input' })
    }
  }

  const handleEnroll = () => {
    if (factorType === 'sms') {
      onEnroll(factorType, { phoneNumber })
    } else if (factorType === 'email') {
      onEnroll(factorType, { email })
    }
  }

  const handleVerify = () => {
    onVerify(factorType, verificationCode)
  }

  const handleResend = async () => {
    if (onResend && !resendLoading && !resendDisabled) {
      setResendLoading(true)
      setResendSuccess(false)
      try {
        await onResend(factorType)
        setResendSuccess(true)
        setResendDisabled(true)
        // Re-enable resend after 30 seconds
        setTimeout(() => {
          setResendDisabled(false)
          setResendSuccess(false)
        }, 30000)
      } catch {
        // Error handling is done in the parent component
      } finally {
        setResendLoading(false)
      }
    }
  }

  const canEnroll = () => {
    if (factorType === 'sms') return isPhoneValid
    if (factorType === 'email') return isEmailValid
    return true
  }

  const renderInput = () => {
    if (factorType === 'sms') {
      return (
        <Box className={classes.phoneInput}>
          <Typography className={classes.inputLabel}>Mobile Number</Typography>
          <PhoneInput
            placeholder="Enter phone number"
            initialValueFormat="national"
            defaultCountry="US"
            value={phoneNumber}
            onChange={(value) => setPhoneNumber(value || '')}
            onCountryChange={(value) => setCountry(value || 'US')}
            disabled={disabled || showLoader}
          />
        </Box>
      )
    }

    if (factorType === 'email') {
      return (
        <Box>
          <Typography className={classes.inputLabel}>Email Address</Typography>
          <TextField
            fullWidth
            placeholder="Enter email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={disabled || showLoader}
            className={classes.emailInput}
            size="small"
          />
        </Box>
      )
    }

    return null
  }

  const renderVerification = () => {
    if (factorType === 'totp') {
      if (state.qrCode) {
        return (
          <TotpSetup
            qrCode={state.qrCode}
            secret={state.secret || ''}
            issuer={state.issuer || 'BrightMLS'}
          />
        )
      }
      // TOTP without QR code - show manual entry instructions
      return (
        <Box>
          <Typography className={classes.inputLabel}>
            Enter the 6-digit code from your authenticator app
          </Typography>
          {state.secret && (
            <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace' }}>
              Secret: {state.secret}
            </Typography>
          )}
        </Box>
      )
    }

    return (
      <Box>
        <Typography className={classes.inputLabel}>
          Enter the 6-digit code sent to your{' '}
          {factorType === 'sms' ? 'phone' : 'email'}
        </Typography>
      </Box>
    )
  }

  const renderOtpInput = () => (
    <Box className={classes.otpContainer}>
      <OtpInput
        value={verificationCode}
        onChange={(value: string) => {
          if (value === '' || /^[0-9]*$/.test(value)) {
            setVerificationCode(value)
          }
        }}
        numInputs={6}
        inputStyle="otp-input"
        containerStyle="otp-input-container"
        focusStyle="otp-input--focused"
        renderInput={(props: object) => (
          <input {...{ ...props, inputMode: 'numeric' }} />
        )}
        shouldAutoFocus={isVerifying}
      />
    </Box>
  )

  return (
    <Box
      className={cx(
        classes.card,
        isEnrolled && classes.cardEnrolled,
        isRequired && !isEnrolled && classes.cardRequired,
      )}
    >
      <Box className={classes.header}>
        <Box className={classes.topRow}>
          <Box className={classes.iconAndTitle}>
            <Box
              className={cx(classes.icon, isEnrolled && classes.iconEnrolled)}
            >
              {getFactorIcon(factorType)}
            </Box>
            <Typography className={classes.title}>
              {getFactorDisplayName(factorType)}
            </Typography>
          </Box>
          <Box className={classes.badges}>
            {isRequired ? (
              <Chip
                label="Required"
                size="small"
                className={classes.requiredChip}
              />
            ) : (
              <Chip
                label="Optional"
                size="small"
                className={classes.optionalChip}
              />
            )}
            {isEnrolled && (
              <Chip
                icon={<CheckCircle fontSize="small" />}
                label="Enrolled"
                size="small"
                className={classes.enrolledChip}
              />
            )}
          </Box>
        </Box>
        <Typography className={classes.description}>
          {getFactorDescription(factorType, userEmail)}
        </Typography>
      </Box>

      {/* Not enrolled - show "Set Up" button (unless locked) */}
      {state.status === 'not_enrolled' && !disabled && !locked && (
        <Box className={classes.inputContainer}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleStartEnrollment}
            disabled={showLoader}
          >
            Set Up {getFactorDisplayName(factorType)}
          </Button>
        </Box>
      )}

      {/* Input mode - show input fields */}
      {isInputMode && (
        <Box className={classes.inputContainer}>
          {renderInput()}
          {state.error && (
            <CustomAlert severity="error" factorType={factorType}>
              {state.error}
            </CustomAlert>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleEnroll}
            disabled={!canEnroll() || showLoader}
            className={classes.enrollButton}
          >
            {showLoader ? 'Sending...' : 'Send Verification Code'}
          </Button>
        </Box>
      )}

      {/* Verifying mode - show OTP input */}
      {isVerifying && (
        <Box className={classes.inputContainer}>
          {renderVerification()}
          {renderOtpInput()}
          {state.error && (
            <CustomAlert severity="error" factorType={factorType}>
              {state.error}
            </CustomAlert>
          )}
          {onResend && factorType !== 'totp' && (
            <Box className={classes.resendContainer}>
              {resendSuccess ? (
                <Typography className={classes.resendSuccess}>
                  Code sent! Check your{' '}
                  {factorType === 'sms' ? 'phone' : 'email'}.
                </Typography>
              ) : (
                <Typography
                  className={cx(
                    classes.resendLink,
                    (resendLoading || resendDisabled) && classes.resendDisabled,
                  )}
                  onClick={handleResend}
                >
                  {resendLoading && (
                    <>
                      <CircularProgress size={12} sx={{ mr: 1 }} />
                      Sending...
                    </>
                  )}
                  {!resendLoading &&
                    resendDisabled &&
                    'Code sent - wait 30s to resend'}
                  {!resendLoading &&
                    !resendDisabled &&
                    "Didn't get the code? Resend"}
                </Typography>
              )}
            </Box>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleVerify}
            disabled={verificationCode.length !== 6 || showLoader}
            className={classes.verifyButton}
          >
            {showLoader ? 'Verifying...' : 'Verify'}
          </Button>
        </Box>
      )}

      {/* Enrolled - show success info */}
      {isEnrolled && (
        <Box className={classes.enrolledInfo}>
          <CheckCircle fontSize="small" />
          <Typography sx={{ ml: 1 }}>
            {factorType === 'sms' &&
              (state.phoneNumber
                ? `Phone: ***-***-${state.phoneNumber.slice(-4)}`
                : 'SMS configured')}
            {factorType === 'email' &&
              (state.email && state.email.includes('@')
                ? `Email: ${state.email.slice(0, 2)}***@${state.email.split('@')[1]}`
                : 'Email configured')}
            {factorType === 'totp' && 'Authenticator app configured'}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default FactorEnrollmentCard
